/**
 * The site's public, login-free pages (W8-RIDER-SAFETY-CONSENT, D440):
 *
 *  - `/v/<token>` — a rider's live-trip link (R-02): the page polls `GET /v1/public/trip-shares/:token` and shows the
 *    ride's state, the car's last position and — when the rider opted in — the driver's first name and the car/plate.
 *    When the link is revoked, expired or the trip has ended, it says so and shows nothing else.
 *  - `/v/g/<token>` — a hotel guest's ride link (D850, L4-001): the page polls `GET /v1/public/guest-rides/:token` and shows
 *    the driver's first name, the car (model, colour, plate), the car's position while it comes to the pickup, the ETA and
 *    the status. It never shows the guest, the pickup or the destination.
 *  - `/legal/<country>/<audience>/<kind>` — the version in force of a terms of use / privacy policy (X-02), from
 *    `GET /v1/public/legal-documents/:country/:audience/:kind`; "not published yet" while the owner's text is a draft.
 *
 * Pure: route parsing, request URLs and the mapping of the API's answer to what the page shows. `web/` renders; the ride's map is
 * `trip-page.ts` + `web/trip-map-engine.ts` (Vello's own basemap, no third-party map link).
 * Neutral: no country, number or language is named here.
 */

export type PublicRoute =
  | { readonly kind: 'trip-share'; readonly token: string }
  | { readonly kind: 'guest-ride'; readonly token: string }
  | { readonly kind: 'legal'; readonly country: string; readonly audience: 'RIDER' | 'DRIVER'; readonly document: 'TERMS_OF_USE' | 'PRIVACY_POLICY' };

const TOKEN = /^[A-Za-z0-9_-]{43}$/;
/** A guest ride link token: 43 characters minted since D850 (32 random bytes), a 36-character id before it. */
const GUEST_TOKEN = /^[A-Za-z0-9_-]{36,64}$/;
const AUDIENCES: Readonly<Record<string, 'RIDER' | 'DRIVER'>> = { rider: 'RIDER', driver: 'DRIVER' };
const DOCUMENTS: Readonly<Record<string, 'TERMS_OF_USE' | 'PRIVACY_POLICY'>> = { terms: 'TERMS_OF_USE', privacy: 'PRIVACY_POLICY' };

/** The public page a path names, or undefined (the institutional site renders it). */
export function publicRouteOf(pathname: string): PublicRoute | undefined {
  const parts = pathname.split('/').filter((p) => p !== '');
  if (parts.length === 2 && parts[0] === 'v' && TOKEN.test(parts[1] ?? '')) return { kind: 'trip-share', token: parts[1] as string };
  if (parts.length === 3 && parts[0] === 'v' && parts[1] === 'g' && GUEST_TOKEN.test(parts[2] ?? '')) return { kind: 'guest-ride', token: parts[2] as string };
  if (parts.length === 4 && parts[0] === 'legal') {
    const country = (parts[1] ?? '').toUpperCase();
    const audience = AUDIENCES[(parts[2] ?? '').toLowerCase()];
    const document = DOCUMENTS[(parts[3] ?? '').toLowerCase()];
    if (/^[A-Z]{2}$/.test(country) && audience !== undefined && document !== undefined) return { kind: 'legal', country, audience, document };
  }
  return undefined;
}

/** The site path of a legal text (what the apps and the footer link to). */
export function legalPagePath(country: string, audience: 'RIDER' | 'DRIVER', document: 'TERMS_OF_USE' | 'PRIVACY_POLICY'): string {
  return `/legal/${country.toLowerCase()}/${audience === 'RIDER' ? 'rider' : 'driver'}/${document === 'TERMS_OF_USE' ? 'terms' : 'privacy'}`;
}

/** The API default when the runtime config names none (the widget's default origin). */
export const DEFAULT_PUBLIC_API_ORIGIN = 'https://api.usevello.app';

/** The API origin the public pages read: the configured one when it is a bare http(s) origin, else the default. No trailing slash. */
export function publicApiOrigin(apiOrigin: string | undefined): string {
  return (apiOrigin !== undefined && /^https?:\/\/[^/\s?#]+\/?$/.test(apiOrigin) ? apiOrigin : DEFAULT_PUBLIC_API_ORIGIN).replace(/\/$/, '');
}

export function publicApiUrl(apiOrigin: string | undefined, route: PublicRoute): string {
  const origin = publicApiOrigin(apiOrigin);
  if (route.kind === 'trip-share') return `${origin}/v1/public/trip-shares/${route.token}`;
  if (route.kind === 'guest-ride') return `${origin}/v1/public/guest-rides/${route.token}`;
  return `${origin}/v1/public/legal-documents/${route.country}/${route.audience}/${route.document}`;
}

/** How often the trip page re-reads the link (the API's public bucket allows it comfortably). */
export const TRIP_SHARE_POLL_MS = 10_000;

export type TripSharePageView =
  | { readonly kind: 'live'; readonly stateMessageId: string; readonly etaMinutes?: number; readonly driverName?: string; readonly vehicle?: string; readonly position?: { readonly latitude: number; readonly longitude: number; readonly updatedAt?: string; readonly headingDegrees?: number }; readonly destination?: { readonly latitude: number; readonly longitude: number }; readonly route?: readonly { readonly latitude: number; readonly longitude: number }[] }
  | { readonly kind: 'ended'; readonly messageId: string }
  | { readonly kind: 'unavailable' };

const STATE_MESSAGE: Readonly<Record<string, string>> = {
  WAITING_FOR_DRIVER: 'site.trip.state.waiting',
  DRIVER_ON_THE_WAY: 'site.trip.state.onTheWay',
  DRIVER_ARRIVED: 'site.trip.state.arrived',
  ON_TRIP: 'site.trip.state.onTrip',
};
/** A guest link answers 200 for a short while after the ride ended, with the final state (D850). */
const FINAL_MESSAGE: Readonly<Record<string, string>> = { COMPLETED: 'site.trip.ended.tripEnded', CANCELLED: 'site.trip.ended.cancelled' };
const ENDED_MESSAGE: Readonly<Record<string, string>> = {
  SHARE_NOT_FOUND: 'site.trip.ended.notFound',
  SHARE_REVOKED: 'site.trip.ended.revoked',
  SHARE_EXPIRED: 'site.trip.ended.expired',
  TRIP_ENDED: 'site.trip.ended.tripEnded',
};

function pointOf(v: unknown): { latitude: number; longitude: number } | undefined {
  const o = typeof v === 'object' && v !== null ? (v as { latitude?: unknown; longitude?: unknown }) : undefined;
  return o !== undefined && typeof o.latitude === 'number' && typeof o.longitude === 'number' && Number.isFinite(o.latitude) && Number.isFinite(o.longitude) && Math.abs(o.latitude) <= 90 && Math.abs(o.longitude) <= 180
    ? { latitude: o.latitude, longitude: o.longitude }
    : undefined;
}

/** Map the API's answer (status + JSON body) to what the trip page shows. */
export function tripSharePageView(status: number, body: unknown): TripSharePageView {
  const o = typeof body === 'object' && body !== null ? (body as Record<string, unknown>) : {};
  if (status === 200 && typeof o.state === 'string' && FINAL_MESSAGE[o.state] !== undefined) return { kind: 'ended', messageId: FINAL_MESSAGE[o.state] as string };
  if (status === 200 && typeof o.state === 'string') {
    const driver = o.driver as { firstName?: unknown } | undefined;
    const v = o.vehicle as { plate?: unknown; make?: unknown; model?: unknown; color?: unknown } | undefined;
    const p = o.position as { latitude?: unknown; longitude?: unknown; updatedAt?: unknown; headingDegrees?: unknown } | undefined;
    const vehicle = v !== undefined && typeof v.plate === 'string'
      ? [v.make, v.model, v.color].filter((x): x is string => typeof x === 'string' && x !== '').concat(v.plate).join(' · ')
      : undefined;
    const destination = pointOf(o.destination);
    // A line is used only when every point is valid; a broken one is dropped whole and the map draws without it.
    const rawRoute = Array.isArray(o.route) ? o.route.map(pointOf) : undefined;
    const route = rawRoute !== undefined && rawRoute.length >= 2 && rawRoute.every((x) => x !== undefined) ? (rawRoute as { latitude: number; longitude: number }[]) : undefined;
    return {
      kind: 'live',
      stateMessageId: STATE_MESSAGE[o.state] ?? 'site.trip.state.waiting',
      ...(typeof o.etaSeconds === 'number' && Number.isFinite(o.etaSeconds) && o.etaSeconds >= 0 ? { etaMinutes: Math.max(1, Math.round(o.etaSeconds / 60)) } : {}),
      ...(typeof driver?.firstName === 'string' ? { driverName: driver.firstName } : {}),
      ...(vehicle !== undefined ? { vehicle } : {}),
      ...(p !== undefined && typeof p.latitude === 'number' && typeof p.longitude === 'number'
        ? { position: { latitude: p.latitude, longitude: p.longitude, ...(typeof p.updatedAt === 'string' ? { updatedAt: p.updatedAt } : {}), ...(typeof p.headingDegrees === 'number' && Number.isFinite(p.headingDegrees) ? { headingDegrees: p.headingDegrees } : {}) } }
        : {}),
      ...(destination !== undefined ? { destination } : {}),
      ...(route !== undefined ? { route } : {}),
    };
  }
  if (status === 404 && typeof o.reasonCode === 'string' && ENDED_MESSAGE[o.reasonCode] !== undefined) return { kind: 'ended', messageId: ENDED_MESSAGE[o.reasonCode] as string };
  return { kind: 'unavailable' };
}
